import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Search, TrendingUp, Zap, Timer, Target, CheckSquare } from 'lucide-react';
import clsx from 'clsx';
import { PageHeader } from '@/components/PageHeader';
import { KpiCard } from '@/components/KpiCard';
import { EditDemandaModalV3 as EditDemandaModal } from '@/components/EditDemandaModalV3';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { displayStatus, type Demanda, type StatusDemanda, type CustomChecklistItem } from '@/lib/types';

const STATUS_COLS: { status: StatusDemanda; color: string }[] = [
  { status: 'Triagem',              color: 'border-blue/40' },
  { status: 'Em produção',          color: 'border-orange/40' },
  { status: 'Em revisão',           color: 'border-purple/40' },
  { status: 'Aguardando aprovação', color: 'border-pink/40' },
  { status: 'Publicada',            color: 'border-green/40' },
];

export function MinhasDemandas() {
  const { user } = useAuth();
  const me = user?.pessoaId ?? '';
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState<string | null>(null);

  const demandasQ = useQuery({ queryKey: ['demandas'], queryFn: () => api.demandas.list().then((r) => r.data) });
  const solicitantesQ = useQuery({ queryKey: ['solicitantes'], queryFn: () => api.solicitantes.list().then((r) => r.data) });
  const pessoasQ = useQuery({ queryKey: ['pessoas'], queryFn: () => api.pessoas.list().then((r) => r.data) });
  const areasQ = useQuery({ queryKey: ['areas'], queryFn: () => api.areas.list().then((r) => r.data) });

  const allDemandas = demandasQ.data ?? [];
  const solicitantes = solicitantesQ.data ?? [];
  const pessoas = pessoasQ.data ?? [];
  const areas = areasQ.data ?? [];

  // Filtra demandas onde sou executor OU coordenador
  const minhas = useMemo(() => {
    if (!me) return [];
    return allDemandas.filter((d) =>
      d.executorId === me || d.coordenadorId === me
      || (d.respBriefingIds ?? []).includes(me)
      || (d.respProducaoIds ?? []).includes(me)
      || (d.respRevisaoIds ?? []).includes(me)
      || (d.respAprovacaoIds ?? []).includes(me)
      || (d.checklistResponsaveis && typeof d.checklistResponsaveis === 'string' && d.checklistResponsaveis.includes(me))
    );
  }, [allDemandas, me]);

  const filtradas = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return minhas;
    return minhas.filter((d) =>
      d.titulo.toLowerCase().includes(q) || (d.pauta ?? '').toLowerCase().includes(q)
    );
  }, [minhas, search]);

  const solById = useMemo(() => new Map(solicitantes.map((s) => [s.id, s.nome])), [solicitantes]);

  const porStatus = useMemo(() => {
    const m = new Map<StatusDemanda, Demanda[]>();
    for (const col of STATUS_COLS) m.set(col.status, []);
    for (const d of filtradas) {
      const arr = m.get(d.status);
      if (arr) arr.push(d);
    }
    return m;
  }, [filtradas]);

  const ativas = filtradas.filter((d) => ['Triagem', 'Em produção', 'Em revisão', 'Aguardando aprovação'].includes(d.status));
  const atrasadas = ativas.filter((d) => d.prazoSla && new Date(d.prazoSla) < new Date());

  // Productivity metrics
  const metrics = useMemo(() => {
    const now = new Date();
    const thisMonth = now.toISOString().slice(0, 7); // YYYY-MM
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().slice(0, 7);

    const publicadas = minhas.filter(d => d.status === 'Publicada');
    const entreguesMes = publicadas.filter(d => d.dataPublicacao?.slice(0, 7) === thisMonth).length;
    const entreguesMesPassado = publicadas.filter(d => d.dataPublicacao?.slice(0, 7) === lastMonth).length;

    // Average SLA compliance (how many delivered on time)
    let onTime = 0;
    let total = 0;
    for (const d of publicadas) {
      if (d.prazoSla && d.dataPublicacao) {
        total++;
        if (d.dataPublicacao.slice(0, 10) <= d.prazoSla.slice(0, 10)) onTime++;
      }
    }
    const slaPct = total > 0 ? Math.round(100 * onTime / total) : null;

    // Weekly load (active items this week)
    const me = pessoas.find(p => p.id === user?.pessoaId);
    const capacidade = me?.capacidadeSemanal ?? null;

    return { entreguesMes, entreguesMesPassado, slaPct, capacidade, totalPublicadas: publicadas.length };
  }, [minhas, pessoas, user]);

  // Checklist items assigned to me across all demandas
  const meusChecklists = useMemo(() => {
    if (!me) return [];
    const items: { demandaId: string; demandaTitulo: string; item: CustomChecklistItem }[] = [];
    for (const d of allDemandas) {
      if (!d.customChecklist) continue;
      try {
        const parsed: CustomChecklistItem[] = JSON.parse(d.customChecklist);
        for (const item of parsed) {
          if (item.responsavelId === me && !item.concluido) {
            items.push({ demandaId: d.id, demandaTitulo: d.titulo, item });
          }
        }
      } catch { /* ignore */ }
    }
    return items;
  }, [allDemandas, me]);

  return (
    <div>
      <PageHeader title="Minhas demandas" />
      <div className="px-6 py-4 space-y-4">
        {/* Dashboard de produtividade */}
        <div className="grid grid-cols-5 gap-2">
          <KpiCard label="Em andamento" color="orange" value={ativas.length}
            delta={atrasadas.length > 0 ? `${atrasadas.length} atrasada${atrasadas.length > 1 ? 's' : ''}` : 'tudo no prazo'}
            icon={<Zap className="w-4 h-4" />} />
          <KpiCard label="Entregas este mês" color="green" value={metrics.entreguesMes}
            delta={metrics.entreguesMesPassado > 0 ? `${metrics.entreguesMesPassado} mês passado` : '—'}
            icon={<TrendingUp className="w-4 h-4" />} />
          <KpiCard label="Total entregues" color="blue" value={metrics.totalPublicadas}
            delta={`de ${minhas.length} atribuídas`}
            icon={<ClipboardList className="w-4 h-4" />} />
          <KpiCard label="SLA atingido" color={metrics.slaPct !== null && metrics.slaPct >= 80 ? 'green' : 'red'}
            value={metrics.slaPct !== null ? `${metrics.slaPct}%` : '—'}
            delta="entregas no prazo"
            icon={<Timer className="w-4 h-4" />} />
          <KpiCard label="Carga semanal" color="blue" value={ativas.length}
            delta={metrics.capacidade ? `de ${metrics.capacidade} máx` : 'sem limite definido'}
            icon={<Target className="w-4 h-4" />} />
        </div>

        {/* Checklist items assigned to me */}
        {meusChecklists.length > 0 && (
          <div className="bg-panel border border-border rounded-xl overflow-hidden">
            <div className="px-3 py-2 border-b border-border bg-panel-2/40 flex items-center gap-2">
              <CheckSquare className="w-3.5 h-3.5 text-purple" />
              <span className="text-xs font-bold">Meus itens de checklist</span>
              <span className="text-[10px] text-muted">({meusChecklists.length} pendente{meusChecklists.length !== 1 ? 's' : ''})</span>
            </div>
            <div className="divide-y divide-border max-h-48 overflow-y-auto">
              {meusChecklists.map(({ demandaId, demandaTitulo, item }) => (
                <button key={`${demandaId}-${item.id}`} type="button" onClick={() => setEditId(demandaId)}
                  className="w-full text-left px-3 py-2 hover:bg-panel-2 transition-colors flex items-center gap-2">
                  <span className="w-3 h-3 rounded border border-border shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{item.titulo}</div>
                    <div className="text-[10px] text-muted truncate">{demandaTitulo} · {item.etapa}{item.prazo ? ` · prazo ${new Date(item.prazo).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}` : ''}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted" />
          <input
            className="input w-full pl-9 text-xs"
            placeholder="Buscar nos meus cards..."
            value={search} onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {demandasQ.isLoading && <div className="card text-sm text-muted">Carregando…</div>}

        {!demandasQ.isLoading && minhas.length === 0 && (
          <div className="card text-center py-12">
            <ClipboardList className="w-10 h-10 text-muted mx-auto mb-3" />
            <p className="text-sm text-muted mb-1">Nenhuma demanda atribuída a você ainda.</p>
            <p className="text-[11px] text-muted">
              Quando você for adicionado como Executor ou Coordenador de uma demanda, ela aparece aqui.
            </p>
          </div>
        )}

        {!demandasQ.isLoading && minhas.length > 0 && (
          <div className="space-y-3">
            {STATUS_COLS.map((col) => {
              const lista = porStatus.get(col.status) ?? [];
              if (lista.length === 0) return null;
              return (
                <section key={col.status}>
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-text-2 mb-1.5">
                    {displayStatus(col.status)} <span className="text-muted font-normal">· {lista.length}</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                    {lista.map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => setEditId(d.id)}
                        className={clsx(
                          'bg-panel border border-border border-2 rounded-md p-3 text-left hover:border-blue/50 hover:bg-panel-2 transition-colors',
                          col.color,
                        )}
                      >
                        <div className="text-xs font-semibold leading-snug line-clamp-2 mb-1">{d.titulo}{(() => {
                  const isDirectAssignee = d.executorId === me || d.coordenadorId === me || (d.respBriefingIds ?? []).includes(me) || (d.respProducaoIds ?? []).includes(me) || (d.respRevisaoIds ?? []).includes(me) || (d.respAprovacaoIds ?? []).includes(me);
                  const isChecklistOnly = !isDirectAssignee && d.checklistResponsaveis && typeof d.checklistResponsaveis === 'string' && d.checklistResponsaveis.includes(user?.pessoaId || 'XXXXX');
                  return isChecklistOnly ? <span className="badge-mini bm-upsell">item</span> : null;
                })()}</div>
                        <div className="text-[10px] text-text-2 truncate mb-1">
                          {solById.get(d.solicitanteId) ?? '—'}
                        </div>
                        <div className="flex items-center justify-between text-[10px]">
                          <span className="text-muted">{d.prazoSla ? `prazo ${new Date(d.prazoSla).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}` : 'sem prazo'}</span>
                          <span className={clsx(
                            'inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase',
                            d.prioridade === 'Crítica' ? 'bg-red text-white'
                              : d.prioridade === 'Alta' ? 'bg-red/15 text-red'
                              : d.prioridade === 'Média' ? 'bg-orange/15 text-orange'
                              : 'bg-green/15 text-green',
                          )}>
                            {d.prioridade}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>

      <EditDemandaModal
        demandaId={editId} demandas={allDemandas}
        solicitantes={solicitantes} pessoas={pessoas} areas={areas}
        onClose={() => setEditId(null)}
      />
    </div>
  );
}
